import { z } from "zod";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Trans, useTranslation } from "react-i18next";
import { cn } from "@/ui/lib/utils.ts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/ui/components/ui/card.tsx";
import { Button } from "@/ui/components/ui/button.tsx";

import TextInputController from "@/shared/components/ui/form/input-with-controller.tsx";
import { Loader2 } from "lucide-react";


const LoginInputSchema = z.object({
  email: z
    .string({
      required_error: "validation.required",
    })
    .min(1, {
      message: "validation.required",
    }),
  password: z.string({
    required_error: "validation.required"
  }).min(1, {
    message: "validation.required"
  }),
});

export type LoginInput = z.infer<typeof LoginInputSchema>;

interface LoginFormProps {
  onSubmit: (data: LoginInput) => void;
  isLoading: boolean;
}

const LoginForm = ({ onSubmit, isLoading }: LoginFormProps) => {
  const { t } = useTranslation("auth");
  const form = useForm<LoginInput>({
    resolver: zodResolver(LoginInputSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const handleSubmit =  async (data: LoginInput) => {
    onSubmit(data)

  };

  return (
    <div className={cn("flex flex-col gap-6 max-w-96 mx-auto")}>
      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-xl">{t("login.title")}</CardTitle>
          <CardDescription>
            {t("login.description")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)}>
              <div className="grid gap-6">
                <div className="grid gap-4">
                  <TextInputController placeholder={t("login.form.usernamePlaceholder")} title={t("login.form.usernameLabel")} name={"email"} />
                  <TextInputController placeholder={t("login.form.passwordPlaceholder")} title={t("login.form.passwordLabel")} type={"password"} name={"password"} />
                  <Button type="submit" className="w-full" disabled={isLoading} >
                    {isLoading && <Loader2 className={"animate-spin"} />}
                    {t("login.form.submit")}
                  </Button>
                </div>
                <div className="text-center text-sm">
                  {t("login.forgotPrompt")}{" "}
                  <a href="#" className="underline underline-offset-4">
                    {t("login.forgotLink")}
                  </a>
                </div>
              </div>
            </form>
          </FormProvider>
        </CardContent>
      </Card>
      <div
        className="text-balance text-center text-xs text-muted-foreground [&_a]:underline [&_a]:underline-offset-4 [&_a]:hover:text-primary  ">
        <Trans
          t={t}
          i18nKey="legal.agreement"
          components={{
            terms: <a href="#" />,
            privacy: <a href="#" />,
          }}
        />
      </div>
    </div>
  )
};

export default LoginForm;
